import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ExternalLink, ArrowLeft, Building2, User, Calendar, Mail, FileText, DollarSign, Eye, ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker with local bundled worker - will be updated with correct path
pdfjs.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';

// Add global error handler for PDF.js
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.filename && event.filename.includes('pdf.worker')) {
      console.error('[PDF] Worker error:', event.error, event.filename);
    }
  });
}

// In-memory cache for invoices data (shared with main list)
interface CacheEntry {
  data: Invoice[];
  timestamp: number;
  expiresAt: number;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
let invoicesCache: CacheEntry | null = null;

interface Project {
  id: string;
  name: string;
}

interface Company {
  id: string;
  name: string;
}

interface Contact {
  id: string;
  name: string;
}

interface Invoice {
  id: string; // Display Invoice ID (e.g., "KJ-INV-0463")
  notionInvID: string; // Main ID used for navigation
  amount: number;
  currency: string;
  dueDate: string;
  tax: boolean;
  companyName: Company[] | null;
  contactName: Contact[] | null;
  status: 'Paid' | 'Open' | 'Planned';
  xeroId?: string;
  xeroUrl?: string;
  project: Project[] | null;
  sentByPost?: boolean;
  sentByEmail?: boolean;
  createdAt?: string;
  updatedAt?: string;
  tenantID?: string;
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Get WordPress config for asset URLs
  const wpConfig = typeof window !== 'undefined' ? (window as any).WP_COCKPIT : null;
  
  // Update PDF.js worker path with correct WordPress assets URL
  useEffect(() => {
    if (wpConfig?.assetsUrl) {
      const workerUrl = wpConfig.assetsUrl.replace('/assets/', '/') + 'assets/pdf.worker.min.js';
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      
      // Add error handling for worker loading
      const isDev = wpConfig?.devMode || false;
      if (isDev) {
        console.log('[PDF] Setting worker URL to:', workerUrl);
        console.log('[PDF] PDF.js version:', pdfjs.version);
        
        // Test if worker file is accessible
        fetch(workerUrl, { method: 'HEAD' })
          .then(response => {
            console.log('[PDF] Worker accessibility test:', {
              url: workerUrl,
              status: response.status,
              statusText: response.statusText,
              contentType: response.headers.get('content-type')
            });
          })
          .catch(error => {
            console.error('[PDF] Worker accessibility test failed:', error);
          });
      }
    }
  }, [wpConfig]);

  const getBillingEntity = (tenantID: string | null | undefined) => {
    if (!tenantID) {
      return { name: 'Not Set', variant: 'secondary' as const, className: 'bg-gray-100 text-gray-600 border-gray-300' };
    }
    
    switch (tenantID) {
      case '75b62360-50ab-40e7-944c-c65197ba7b68':
        return { name: 'Kainjoo SA', variant: 'default' as const, className: 'bg-primary text-primary-foreground border-primary' };
      case 'abc6d49d-db14-4f90-99f9-e4231b1cc274':
        return { name: 'Kainjoo Inc', variant: 'secondary' as const, className: 'bg-blue-100 text-blue-800 border-blue-300' };
      default:
        return { name: 'Unknown Entity', variant: 'secondary' as const, className: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    }
  };

  const fetchInvoiceDetail = async () => {
    try {
      setLoading(true);

      // Check in-memory cache first
      if (invoicesCache) {
        const now = Date.now();
        if (now < invoicesCache.expiresAt) {
          const wp = (window as any).WP_COCKPIT;
          const isDev = wp?.devMode || false;
          if (isDev) console.log('[InvoiceDetailPage] Using cached data');
          const foundInvoice = invoicesCache.data.find((inv: Invoice) => inv.notionInvID === id);
          if (foundInvoice) {
            setInvoice(foundInvoice);
            setLoading(false);
            return;
          }
        } else {
          const wp = (window as any).WP_COCKPIT;
          const isDev = wp?.devMode || false;
          if (isDev) console.log('[InvoiceDetailPage] Cache expired');
          invoicesCache = null;
        }
      }

      // Build headers with nonce
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add WP REST nonce header if provided by server-localized object
      try {
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) {
          console.log('[fetchInvoiceDetail] Invoice ID:', id);
          console.log('[fetchInvoiceDetail] WP_COCKPIT object:', wp);
        }
        if (wp && wp.restNonce) {
          headers['X-WP-Nonce'] = String(wp.restNonce);
          if (isDev) console.log('[fetchInvoiceDetail] Added nonce to headers:', headers['X-WP-Nonce']);
        } else {
          console.error('No restNonce found in WP_COCKPIT');
        }
      } catch (e) {
        console.error('Error accessing WP_COCKPIT:', e);
      }

      const fetchOptions: RequestInit = { 
        credentials: 'same-origin', 
        headers 
      };

      const response = await fetch(`/wp-json/kj-cockpit/v1/fetch-invoices`, fetchOptions);

      if (!response.ok) {
        if (response.status === 502) {
          throw new Error('The server is taking longer than expected to process the request. Please try again in a moment.');
        } else if (response.status === 504) {
          throw new Error('Request timed out. The invoice data processing is taking longer than expected.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        // Update in-memory cache
        if (Array.isArray(data.data)) {
          invoicesCache = {
            data: data.data,
            timestamp: Date.now(),
            expiresAt: Date.now() + CACHE_DURATION,
          };
          const wp = (window as any).WP_COCKPIT;
          const isDev = wp?.devMode || false;
          if (isDev) console.log('[InvoiceDetailPage] Updated cache with fresh data');
        }
        
        // Find the specific invoice by notionInvID
        const foundInvoice = data.data.find((inv: Invoice) => inv.notionInvID === id);
        if (foundInvoice) {
          setInvoice(foundInvoice);
        } else {
          toast.error('Invoice not found');
          navigate('/invoices');
        }
      } else {
        console.error('Failed to fetch invoice:', data);
        toast.error('Failed to load invoice');
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Error loading invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoicePdf = async (xeroid: string) => {
    try {
      setPdfLoading(true);
      
      // Build headers with nonce
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add WP REST nonce header if provided by server-localized object
      try {
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) {
          console.log('[fetchInvoicePdf] Xero ID:', xeroid);
          console.log('[fetchInvoicePdf] WP_COCKPIT object:', wp);
        }
        if (wp && wp.restNonce) {
          headers['X-WP-Nonce'] = String(wp.restNonce);
          if (isDev) console.log('[fetchInvoicePdf] Added nonce to headers:', headers['X-WP-Nonce']);
        } else {
          console.error('No restNonce found in WP_COCKPIT');
        }
      } catch (e) {
        console.error('Error accessing WP_COCKPIT:', e);
      }

      const response = await fetch(`/wp-json/kj-cockpit/v1/fetch-invoice-pdf/${encodeURIComponent(xeroid)}`, {
        method: 'GET',
        headers,
        credentials: 'same-origin'
      });

      const wpConfig2 = (window as any).WP_COCKPIT;
      const isDev2 = wpConfig2?.devMode || false;
      if (isDev2) {
        console.log('[fetchInvoicePdf] Response status:', response.status);
        console.log('[fetchInvoicePdf] Response headers:', Object.fromEntries(response.headers.entries()));
      }

      if (!response.ok) {
        const errorText = await response.text();
        if (isDev2) {
          console.error('[fetchInvoicePdf] Error response:', errorText);
        }
        
        if (response.status === 502) {
          throw new Error('The server is taking longer than expected to fetch the PDF. Please try again.');
        } else if (response.status === 504) {
          throw new Error('Request timed out while fetching PDF.');
        } else {
          throw new Error(`Failed to fetch PDF: HTTP ${response.status}`);
        }
      }

      // Get the PDF data - backend now handles base64 decoding
      const pdfBlob = await response.blob();
      
      // Verify it's actually a PDF
      if (pdfBlob.type !== 'application/pdf' && !pdfBlob.type.includes('pdf')) {
        console.warn('Response is not a PDF:', pdfBlob.type);
        // Try anyway, might be a server not setting correct content-type
      }
      
      // Basic validation
      if (pdfBlob.size === 0) {
        throw new Error('PDF file is empty');
      }

      // Quick validation - check if blob starts with PDF signature
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const pdfSignature = String.fromCharCode(...uint8Array.slice(0, 4));
      
      if (isDev2) {
        console.log('[fetchInvoicePdf] PDF size:', uint8Array.length);
        console.log('[fetchInvoicePdf] PDF signature:', pdfSignature);
        console.log('[fetchInvoicePdf] First 10 bytes:', Array.from(uint8Array.slice(0, 10)));
      }
      
      if (!pdfSignature.startsWith('%PDF')) {
        console.error('Invalid PDF file - missing PDF signature');
        toast.error('Invalid PDF file received from server');
        return;
      }

      // Create blob URL from the PDF data
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setPdfDialogOpen(true);
      
      if (isDev2) {
        console.log('[fetchInvoicePdf] PDF blob created successfully:', {
          size: uint8Array.length,
          type: pdfBlob.type,
          url: pdfUrl,
          signature: pdfSignature
        });
      }
      
    } catch (error) {
      console.error('Error fetching PDF:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // Clean up PDF URL when component unmounts or dialog closes
  const cleanupPdfUrl = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPageNumber(1);
    setNumPages(0);
  };

  const fetchPaymentLink = async (xeroid: string) => {
    try {
      setPaymentLoading(true);
      
      // Build headers with nonce
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add WP REST nonce header if provided by server-localized object
      try {
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) {
          console.log('[fetchPaymentLink] Xero ID:', xeroid);
        }
        if (wp && wp.restNonce) {
          headers['X-WP-Nonce'] = String(wp.restNonce);
          if (isDev) console.log('[fetchPaymentLink] Added nonce to headers');
        } else {
          console.error('No restNonce found in WP_COCKPIT');
        }
      } catch (e) {
        console.error('Error accessing WP_COCKPIT:', e);
      }

      const response = await fetch(`/wp-json/kj-cockpit/v1/fetch-payment-link/${encodeURIComponent(xeroid)}`, {
        method: 'GET',
        headers,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[fetchPaymentLink] Error response:', errorText);
        throw new Error(`Failed to fetch payment link: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data && data.link) {
        // Open payment link in a new tab
        window.open(data.link, '_blank', 'noopener,noreferrer');
        toast.success('Payment link opened in new tab');
      } else {
        throw new Error('No payment link found in response');
      }
      
    } catch (error) {
      console.error('Error fetching payment link:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to get payment link');
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    return cleanupPdfUrl;
  }, []);

  useEffect(() => {
    if (id) {
      fetchInvoiceDetail();
    }
  }, [id]);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(dateString));
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'No date';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Paid':
        return { variant: 'default' as const, className: 'badge-paid' };
      case 'Open':
        return { variant: 'secondary' as const, className: 'badge-open' };
      case 'Planned':
        return { variant: 'outline' as const, className: 'badge-planned' };
      default:
        return { variant: 'outline' as const, className: 'badge-planned' };
    }
  };

  const getDueDateStatus = (dueDate: string, status: string) => {
    if (!dueDate) return null;
    
    // Don't show overdue status for paid invoices
    if (status === 'Paid') return null;
    
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { label: 'Overdue', variant: 'destructive' as const, className: 'badge-overdue' };
    } else if (diffDays <= 7) {
      return { label: 'Due Soon', variant: 'secondary' as const };
    } else if (diffDays <= 30) {
      return { label: 'Due This Month', variant: 'outline' as const };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invoice Not Found</h2>
            <p className="text-muted-foreground">The requested invoice could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const dueDateStatus = getDueDateStatus(invoice.dueDate, invoice.status);

  const invoiceCoverUrl = wpConfig?.assetsUrl 
    ? wpConfig.assetsUrl.replace('/assets/', '/') + 'invoicecover.webp'
    : '/invoicecover.webp';

  return (
    <div className="container mx-auto p-6">
      {/* Header with Cover Image */}
      <div className="relative mb-6">
        {/* Cover Image - Made taller */}
        <div className="w-full h-72 rounded-lg overflow-hidden relative">
          <img 
            src={invoiceCoverUrl} 
            alt="Invoice Cover"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to a gradient background if image fails to load
              (e.target as HTMLImageElement).style.display = 'none';
              const parent = (e.target as HTMLImageElement).parentElement;
              if (parent) {
                parent.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
              }
            }}
          />
          
          {/* Dark overlay for better text readability - slightly darker */}
          <div className="absolute inset-0 bg-black/50"></div>
          
          {/* Header Content Overlaid on Image */}
          <div className="absolute inset-0 flex flex-col justify-between p-6 text-white">
            {/* Top: Back Button and PDF Button */}
            <div className="flex justify-between items-start">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/invoices')}
                className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Invoices
              </Button>
              
              {/* PDF View Button - only show if xeroid exists */}
              {invoice.xeroId && (
                <Button 
                  variant="secondary" 
                  onClick={() => fetchInvoicePdf(invoice.xeroId!)}
                  disabled={pdfLoading}
                  className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {pdfLoading ? 'Loading...' : 'View PDF'}
                </Button>
              )}

              {/* Payment Button - only show if not paid and has xeroid */}
              {invoice.status !== 'Paid' && invoice.xeroId && (
                <Button 
                  variant="default" 
                  onClick={() => fetchPaymentLink(invoice.xeroId!)}
                  disabled={paymentLoading}
                  className="bg-primary/90 backdrop-blur-sm text-primary-foreground hover:bg-primary border-0"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {paymentLoading ? 'Loading...' : 'Pay Invoice'}
                </Button>
              )}
            </div>
            
            {/* Bottom: Invoice Info and Status */}
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-white drop-shadow-lg">
                  Invoice {invoice.id}
                </h1>
                <p className="text-white/90 text-lg drop-shadow">
                  {invoice.createdAt ? `Created ${formatDateTime(invoice.createdAt)}` : 'Creation date not available'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant={getStatusVariant(invoice.status).variant} 
                  className={`text-sm ${getStatusVariant(invoice.status).className} bg-white/90 text-gray-900 border-0`}
                >
                  {invoice.status}
                </Badge>
                {dueDateStatus && (
                  <Badge 
                    variant={dueDateStatus.variant} 
                    className={`text-sm ${dueDateStatus.className || 'bg-white/90 text-gray-900'} border-0`}
                  >
                    {dueDateStatus.label}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Invoice Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Invoice Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Amount</label>
                <p className="text-2xl font-bold">{formatCurrency(invoice.amount, invoice.currency)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Currency</label>
                <p className="text-lg font-medium">{invoice.currency}</p>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Billing Entity</label>
              <div className="mt-1">
                <Badge 
                  variant={getBillingEntity(invoice.tenantID).variant}
                  className={getBillingEntity(invoice.tenantID).className}
                >
                  {getBillingEntity(invoice.tenantID).name}
                </Badge>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Due Date</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <p className="font-medium">{formatDate(invoice.dueDate)}</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tax Included</label>
              <div className="mt-1">
                <Badge variant={invoice.tax ? "default" : "secondary"}>
                  {invoice.tax ? "Tax Included" : "No Tax"}
                </Badge>
              </div>
            </div>

            {invoice.xeroUrl && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Xero Link</label>
                <Button variant="outline" size="sm" asChild>
                  <a href={invoice.xeroUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in Xero
                  </a>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Companies</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {invoice.companyName && invoice.companyName.length > 0 ? (
                  invoice.companyName.map((company, index) => (
                    <Badge key={index} variant="outline">
                      {company.name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">No company</span>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Contacts</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {invoice.contactName && invoice.contactName.length > 0 ? (
                  invoice.contactName.map((contact, index) => (
                    <Badge key={index} variant="outline">
                      <User className="h-3 w-3 mr-1" />
                      {contact.name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">No contact</span>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Projects</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {invoice.project && invoice.project.length > 0 ? (
                  invoice.project.map((project, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        console.log('Navigate to project:', project.id);
                      }}
                    >
                      {project.name}
                    </Button>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">No project</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Communication Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Communication Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sent by Email</label>
                <div className="mt-1">
                  <Badge variant={invoice.sentByEmail ? "default" : "secondary"}>
                    {invoice.sentByEmail ? "Sent" : "Not Sent"}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sent by Post</label>
                <div className="mt-1">
                  <Badge variant={invoice.sentByPost ? "default" : "secondary"}>
                    {invoice.sentByPost ? "Sent" : "Not Sent"}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meta Information */}
        <Card>
          <CardHeader>
            <CardTitle>Meta Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Invoice ID</label>
              <p className="font-mono text-sm">{invoice.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Notion ID</label>
              <p className="font-mono text-sm break-all">{invoice.notionInvID}</p>
            </div>
            {invoice.xeroId && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Xero ID</label>
                <p className="font-mono text-sm break-all">{invoice.xeroId}</p>
              </div>
            )}
            {invoice.updatedAt && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                <p className="text-sm">{formatDateTime(invoice.updatedAt)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* PDF Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={(open) => {
        setPdfDialogOpen(open);
        if (!open) {
          cleanupPdfUrl();
        }
      }}>
        <DialogContent className="!max-w-5xl w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] h-[95vh] p-0 sm:!max-w-5xl">
          <DialogHeader className="p-6 pb-2 pr-16">
            <DialogTitle className="flex items-center justify-between pr-2">
              <div className="flex items-center gap-2">
                <img 
                  src={wpConfig?.assetsUrl 
                    ? wpConfig.assetsUrl.replace('/assets/', '/') + 'invoiceIcon.svg'
                    : '/invoiceIcon.svg'} 
                  alt="Invoice Icon"
                  className="w-5 h-5"
                  onError={(e) => {
                    // Fallback to FileText icon if SVG fails to load
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'inline-block';
                  }}
                />
                <FileText className="w-5 h-5" style={{ display: 'none' }} />
                Invoice PDF - {invoice?.id}
              </div>
              {pdfUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = pdfUrl;
                    link.download = `invoice-${invoice?.id || 'unknown'}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center gap-1 mr-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>
              View and download the invoice PDF document
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            {pdfUrl ? (
              <div className="w-full h-full flex flex-col">
                {/* PDF Navigation */}
                <div className="flex items-center justify-between mb-4 p-2 bg-gray-50 rounded flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(page => Math.max(1, page - 1))}
                      disabled={pageNumber <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {pageNumber} of {numPages || '?'}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(page => Math.min(numPages, page + 1))}
                      disabled={pageNumber >= numPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* PDF Viewer */}
                <div className="flex-1 flex items-center justify-center bg-gray-100 rounded overflow-hidden min-h-0">
                  <div className="max-w-full max-h-full flex justify-center overflow-auto">
                    <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => {
                      setNumPages(numPages);
                      setPageNumber(1);
                      console.log('[PDF] Successfully loaded PDF with', numPages, 'pages');
                    }}
                    onLoadError={(error) => {
                      console.error('[PDF] Load error details:', {
                        message: error?.message,
                        name: error?.name,
                        stack: error?.stack,
                        details: (error as any)?.details,
                        workerSrc: pdfjs.GlobalWorkerOptions.workerSrc,
                        pdfUrl: pdfUrl,
                        errorObject: error
                      });
                      toast.error('Failed to load PDF. The file may be corrupted or invalid.');
                    }}
                    loading={
                      <div className="flex items-center justify-center p-8">
                        <div className="text-center">
                          <img 
                            src={wpConfig?.assetsUrl 
                              ? wpConfig.assetsUrl.replace('/assets/', '/') + 'invoiceIcon.svg'
                              : '/invoiceIcon.svg'} 
                            alt="Invoice Icon"
                            className="w-8 h-8 mx-auto mb-2 opacity-50"
                          />
                          <p className="text-sm text-gray-500">Loading PDF...</p>
                        </div>
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center p-8">
                        <div className="text-center">
                          <p className="text-red-600 mb-4">Failed to load PDF</p>
                          <p className="text-sm text-gray-500 mb-4">
                            The PDF file may be corrupted or invalid.
                          </p>
                          <Button
                            variant="outline"
                            onClick={() => {
                              if (pdfUrl) {
                                const link = document.createElement('a');
                                link.href = pdfUrl;
                                link.download = `invoice-${invoice?.id || 'unknown'}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                            Try Download
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      // Use a simple, stable width calculation based on viewport
                      width={Math.min(900, window.innerWidth * 0.65)}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      className="shadow-lg max-w-full h-auto"
                      onLoadError={(error) => {
                        console.error('Page load error:', error);
                      }}
                    />
                    </Document>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <img 
                    src={wpConfig?.assetsUrl 
                      ? wpConfig.assetsUrl.replace('/assets/', '/') + 'invoiceIcon.svg'
                      : '/invoiceIcon.svg'} 
                    alt="Invoice Icon"
                    className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" style={{ display: 'none' }} />
                  <p className="text-muted-foreground">Loading PDF...</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}